// Shared interpretation of the F1-F3 footprint backend error contracts,
// used by Register, Settings, and the Pricing checkout error branch so the
// three surfaces render plan-limit and lane errors identically.
//
// Contracts honored (key off `code`, never the human string):
//  - 400 { error, code: 'FOOTPRINT_EXCEEDS_PLAN' }
//      plan-limit rejection at signup / checkout / both edits.
//      Render `error` verbatim, styled as a plan-limit notice.
//  - 409 { failing_pairs: [{ category, city, reason }] }
//      full-lane rejection on settings edits (same shape checkout uses).
//      reason 'cap-full'        -> "This category-city lane is full."
//      reason 'cap-row-missing' -> "This category-city lane is not
//                                   currently available."

export const FOOTPRINT_EXCEEDS_PLAN = 'FOOTPRINT_EXCEEDS_PLAN';

export interface FailingPair {
  category?: string;
  city?: string;
  reason?: 'cap-full' | 'cap-row-missing' | string;
}

export type FootprintError =
  | { kind: 'plan-limit'; message: string }
  | { kind: 'lanes'; pairs: FailingPair[] }
  | null;

// Human copy for a lane failure reason. Deliberately free of database
// language — see the F4-a spec.
export function laneReasonText(reason?: string): string {
  if (reason === 'cap-full') return 'This category-city lane is full.';
  if (reason === 'cap-row-missing') return 'This category-city lane is not currently available.';
  // Unknown/missing reason: stay generic but lane-shaped, never leak a code.
  return 'This category-city lane is not currently available.';
}

// A short label for a failing pair, e.g. "Plumbing - Walker".
// Falls back gracefully when either side is absent.
export function failingPairLabel(pair: FailingPair, formatCategory: (slug: string) => string): string {
  const cat = pair.category ? formatCategory(pair.category) : '';
  const city = pair.city || '';
  if (cat && city) return `${cat} - ${city}`;
  return cat || city || 'Lane';
}

// Classify an axios-style error (or any thrown value) against the footprint
// contracts. Returns null when it is not a footprint error, so callers can
// fall back to their existing generic error handling.
export function classifyFootprintError(err: any): FootprintError {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (!data) return null;

  if (data.code === FOOTPRINT_EXCEEDS_PLAN) {
    return {
      kind: 'plan-limit',
      message: typeof data.error === 'string' ? data.error : 'This footprint exceeds your current plan.',
    };
  }

  if (status === 409 && Array.isArray(data.failing_pairs)) {
    return { kind: 'lanes', pairs: data.failing_pairs as FailingPair[] };
  }

  return null;
}
